import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, RotateCw, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { TilesetProgressView } from '@/common/data-upload';
import { Button } from '@/components/ui/button';
import { useTilesetUpload } from '@/context/TilesetUploadContext';
import { humanDuration } from '@/lib/utils/duration';
import { getActiveTilesets } from '@/queries/data-uploads/get-active-tilesets';

// Matches the watcher's cadence; both read the same cached query.
const ACTIVE_REFETCH_MS = 15 * 1000;

const CardStatus = {
	UPLOADING: 'uploading',
	QUEUED: 'queued',
	PROCESSING: 'processing',
	READY: 'ready',
	FAILED: 'failed',
} as const;
type CardStatus = (typeof CardStatus)[keyof typeof CardStatus];

type CardItem = {
	key: string;
	fileName: string;
	sizeMB: number | null;
	status: CardStatus;
	/** Browser-side transfer percent; only meaningful while uploading. */
	progress: number;
	stage?: string;
	progressView?: TilesetProgressView | null;
	error?: string;
	/** Only the upload this tab started can be cancelled, retried or dismissed. */
	owned: boolean;
};

/**
 * Bottom-right stack of everything currently converting, not just the upload
 * this tab started.
 *
 * Conversions outlive the tab that started them, and a second upload used to
 * replace the first one's card — the first kept converting with nothing on
 * screen, and a reload showed no progress at all. So the server's list of
 * running conversions is the source of truth here, and the local upload is
 * merged in for the phase the server cannot see yet: the browser-to-S3
 * transfer, and the terminal card the user has not dismissed.
 */
export function FloatingUploadManager() {
	const { t } = useTranslation();
	const { activeUpload, cancel, retry, dismiss } = useTilesetUpload();
	const { data: activeTilesets } = useQuery({
		queryKey: ['data-uploads', 'active-tilesets'],
		queryFn: getActiveTilesets,
		refetchInterval: ACTIVE_REFETCH_MS,
	});

	const items: CardItem[] = [];

	if (activeUpload) {
		items.push({
			key: activeUpload.tempId,
			fileName: activeUpload.fileName,
			sizeMB: activeUpload.sizeMB,
			status: activeUpload.status,
			progress: activeUpload.progress,
			stage: activeUpload.stage,
			progressView: activeUpload.progressView,
			error: activeUpload.error,
			owned: true,
		});
	}

	// Server-side conversions, minus the one the local card already shows.
	for (const upload of activeTilesets ?? []) {
		// DataUpload is a Partial type; a row without an id cannot be keyed or tracked.
		if (!upload.id) continue;
		if (activeUpload?.dataUploadId === upload.id) continue;
		items.push({
			key: upload.id,
			fileName: upload.name ?? t('files.tileset.untitledModel'),
			sizeMB: upload.fileSizeInKB ? upload.fileSizeInKB / 1024 : null,
			// A queued job has not started: saying "processing" over a full bar reads
			// as almost done, which is the opposite of the truth.
			status: upload.tileset?.status === 'queued' ? CardStatus.QUEUED : CardStatus.PROCESSING,
			progress: 0,
			stage: upload.tileset?.stage ?? undefined,
			progressView: upload.tileset?.progressView ?? null,
			owned: false,
		});
	}

	if (items.length === 0) return null;

	return (
		<div className="fixed bottom-20 right-4 z-[9998] flex w-80 flex-col gap-2">
			{items.map((item) => (
				<UploadCard key={item.key} item={item} onCancel={cancel} onRetry={retry} onDismiss={dismiss} />
			))}
		</div>
	);
}

function UploadCard({
	item,
	onCancel,
	onRetry,
	onDismiss,
}: {
	item: CardItem;
	onCancel: () => void;
	onRetry: () => void;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { fileName, sizeMB, status, progress, stage, progressView, error, owned } = item;

	// Include the pipeline stage (e.g. "Final compression") while processing.
	const processingLabel = stage ? t('files.tileset.processingStage', { stage }) : t('files.tileset.processingGeneric');
	const statusLabel =
		status === CardStatus.UPLOADING
			? t('files.tileset.uploading', { progress })
			: status === CardStatus.QUEUED
				? t('files.tileset.queued')
				: status === CardStatus.PROCESSING
					? processingLabel
					: status === CardStatus.READY
						? t('files.tileset.ready')
						: t('files.tileset.failed');

	// A converting job knows how far along it is; before the first estimate
	// arrives the bar stays full and the stage name carries the information. A
	// queued one has done nothing yet, so its bar is empty.
	const barWidth =
		status === CardStatus.QUEUED
			? 0
			: status === CardStatus.UPLOADING
				? progress
				: status === CardStatus.PROCESSING && progressView
					? progressView.percent
					: 100;
	const barColor =
		status === CardStatus.FAILED ? 'bg-red-500' : status === CardStatus.READY ? 'bg-green-600' : 'bg-primary';

	return (
		<div className="rounded-lg border bg-card shadow-lg">
			<div className="flex items-start justify-between gap-2 p-3">
				<div className="flex min-w-0 items-center gap-2">
					{status === CardStatus.UPLOADING || status === CardStatus.PROCESSING || status === CardStatus.QUEUED ? (
						<Loader2 className="size-4 shrink-0 animate-spin text-primary" />
					) : status === CardStatus.READY ? (
						<CheckCircle2 className="size-4 shrink-0 text-green-600" />
					) : (
						<XCircle className="size-4 shrink-0 text-red-500" />
					)}
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-sm font-medium">{fileName}</span>
						{sizeMB !== null && <span className="text-[10px] text-muted-foreground">{sizeMB.toFixed(2)} MB</span>}
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-1">
					{owned && status === CardStatus.FAILED && (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="size-6"
							onClick={onRetry}
							title={t('common.actions.retry')}
						>
							<RotateCw className="size-4" />
						</Button>
					)}
					{owned && status === CardStatus.UPLOADING && (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="size-6 hover:text-red-500"
							onClick={onCancel}
							title={t('common.actions.cancel')}
						>
							<X className="size-4" />
						</Button>
					)}
					{owned && (status === CardStatus.READY || status === CardStatus.FAILED) && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-6 px-2 text-xs"
							onClick={onDismiss}
							title={t('common.actions.close')}
						>
							{t('common.actions.close')}
						</Button>
					)}
				</div>
			</div>
			<div className="px-3 pb-3">
				<div className="mb-1 text-[11px] text-muted-foreground">{statusLabel}</div>
				<div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
					<div className={`h-full transition-all ${barColor}`} style={{ width: `${barWidth}%` }} />
				</div>
				{status === CardStatus.UPLOADING && (
					<p className="mt-1 text-[10px] text-muted-foreground">{t('files.tileset.keepOpenHint')}</p>
				)}
				{status === CardStatus.PROCESSING && progressView && (
					<p className="mt-1 text-[10px] text-muted-foreground">
						{progressView.percent.toFixed(1)}%
						{progressView.work
							? ` · ${t('files.tileset.tilesProgress', { done: progressView.work.done, total: progressView.work.total })}`
							: ''}{' '}
						· {t('files.tileset.elapsedTime', { elapsed: humanDuration(progressView.elapsedSeconds) })} ·{' '}
						{/* Null for the first few percent, where extrapolating is noise. */}
						{progressView.estimatedRemainingSeconds === null
							? t('files.tileset.estimating')
							: t('files.tileset.remainingTime', {
									remaining: humanDuration(progressView.estimatedRemainingSeconds),
								})}
					</p>
				)}
				{(status === CardStatus.PROCESSING || status === CardStatus.QUEUED) && (
					<p className="mt-1 text-[10px] text-muted-foreground">{t('files.tileset.serverRunHint')}</p>
				)}
				{error && status === CardStatus.FAILED && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
			</div>
		</div>
	);
}
